import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import { bool, func, objectOf, shape, string } from "prop-types";

import { RecoveryUserpassStep0 } from "@ui-kit/components/index.es";
import ReCAPTCHA from "react-google-recaptcha";
import { actions as layoutActions } from "reducers/layout";
import { actions as recoveryPasswordActions, selectors as recoveryPasswordSelectors } from "reducers/recoveryPassword";
import * as i18n from "util/i18n";
import * as configUtil from "util/config";
import { isMobileNative } from "util/device";
import Yup from "yup";

import documentTypeFormats from "../../utils/documentTypeFormats";

const DOC_TYPES = [
    { value: "DNI", label: "DNI" },
    { value: "CUIT", label: "CUIT" },
    { value: "CUIL", label: "CUIL" },
    { value: "CDI", label: "CDI" },
];

function RecoveryUserpassStep0V3({
    dispatch,
    location,
    history,
    document: docFromRedux,
    documentType: docTypeFromRedux,
    isRecoveryPassAndUserpass,
    recoveryAsyncPending,
    fieldErrors,
    showCaptcha,
    resetCaptcha,
}) {
    const captchaValueRef = useRef("");
    const recaptchaRef = useRef(null);
    const wrapperRef = useRef(null);
    const shouldShowCaptchaRef = useRef(false);
    const [captchaPortalTarget, setCaptchaPortalTarget] = useState(null);
    const [captchaError, setCaptchaError] = useState("");

    useEffect(() => {
        dispatch(layoutActions.setScopes(["recoveryUserpass"]));
        dispatch(recoveryPasswordActions.recoveryPassCleanUp());
        return () => {
            dispatch(layoutActions.setScopes([]));
        };
    }, [dispatch]);

    const captchaEnabled = configUtil.getBoolean("core.auth.captcha.enabled");
    const numberRetryShowCaptcha = configUtil.get("core.auth.recoverCredential.maxAttemptsBeforeCaptcha");
    const shouldShowCaptcha = (numberRetryShowCaptcha === "0" && captchaEnabled) || (captchaEnabled && showCaptcha);
    shouldShowCaptchaRef.current = shouldShowCaptcha;

    useLayoutEffect(() => {
        if (!shouldShowCaptcha || !wrapperRef.current) {
            setCaptchaPortalTarget(null);
            return undefined;
        }

        const form = wrapperRef.current.querySelector("form");
        if (!form) {
            return undefined;
        }

        const grid = form.firstElementChild;
        if (!grid) {
            return undefined;
        }

        const submitBtn = form.querySelector('button[type="submit"]');
        if (!submitBtn) {
            return undefined;
        }

        let btnCol = submitBtn;
        while (btnCol && btnCol.parentElement !== grid) {
            btnCol = btnCol.parentElement;
        }

        if (!btnCol) {
            return undefined;
        }

        const container = document.createElement("div");
        container.style.width = "100%";
        container.style.gridColumn = "1 / -1";
        grid.insertBefore(container, btnCol);
        setCaptchaPortalTarget(container);

        return () => {
            if (container.parentNode) {
                container.parentNode.removeChild(container);
            }
        };
    }, [shouldShowCaptcha]);

    const initialValues = useMemo(() => {
        const docFromLocation = location?.state?.document;
        const docTypeFromLocation = location?.state?.documentType;
        const doc = docFromLocation ?? docFromRedux ?? "";
        const docType = docTypeFromLocation ?? docTypeFromRedux ?? "DNI";
        return {
            document: {
                number: doc,
                type: { value: docType, label: docType },
            },
            password: "",
        };
    }, [location, docFromRedux, docTypeFromRedux]);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                document: Yup.object().shape({
                    number: Yup.string().required(i18n.get("recoveryUserpass.step1.document.required")),
                    type: Yup.mixed(),
                }),
                password: !isRecoveryPassAndUserpass
                    ? Yup.string().required(i18n.get("recoveryUserpass.step1.password.required"))
                    : Yup.string(),
            }),
        [isRecoveryPassAndUserpass],
    );

    const handleCaptchaChange = useCallback((value) => {
        captchaValueRef.current = value || "";
        setCaptchaError("");
    }, []);

    useEffect(() => {
        if (resetCaptcha && recaptchaRef.current) {
            recaptchaRef.current.reset();
            captchaValueRef.current = "";
        }
    }, [resetCaptcha]);

    const handleSubmit = useCallback(
        (values, formikBag) => {
            const docType = values.document?.type?.value ?? values.document?.type ?? "DNI";
            const document = values.document?.number ?? "";
            const password = values.password ?? "";
            const captcha = captchaValueRef.current || "";

            if (shouldShowCaptchaRef.current && !captcha) {
                setCaptchaError(i18n.get("recoveryUserpass.step1.captcha.required"));
                formikBag.setSubmitting(false);
                return;
            }

            const formatValidation = documentTypeFormats(docType, document);
            if (!formatValidation) {
                const formatMsg = i18n.get("login.step1.wrong.format");
                formikBag.setFieldError("document.number", formatMsg);
                dispatch(
                    recoveryPasswordActions.setFormFieldErrors({
                        "document.number": formatMsg,
                    }),
                );
                formikBag.setSubmitting(false);
                return;
            }

            const enhancedBag = {
                ...formikBag,
                props: { ...(formikBag.props || {}), location, history, isMobileNative },
                setValues: (vals) => {
                    const doc = typeof vals.document === "string" ? vals.document : vals.document?.number ?? "";
                    const docTypeVal = vals.documentType || vals.document?.type?.value || "DNI";
                    formikBag.setValues({
                        document: { number: doc, type: { value: docTypeVal, label: docTypeVal } },
                        password: vals.password ?? "",
                    });
                    captchaValueRef.current = "";
                    if (recaptchaRef.current) {
                        recaptchaRef.current.reset();
                    }
                },
                setErrors: (errs) => {
                    const mapped = {};
                    if (errs.document) {
                        mapped["document.number"] = errs.document;
                    }
                    if (errs.password) {
                        mapped.password = errs.password;
                    }
                    formikBag.setErrors(mapped);
                    if (errs.captcha) {
                        setCaptchaError(errs.captcha);
                    }
                },
            };

            setCaptchaError("");
            dispatch(recoveryPasswordActions.setResetCaptcha(false));
            if (!isRecoveryPassAndUserpass) {
                dispatch(recoveryPasswordActions.removeRecoveryPassAndUserpass());
            }
            dispatch(
                recoveryPasswordActions.recoveryUserpassStep0(
                    password,
                    document,
                    docType,
                    enhancedBag,
                    isRecoveryPassAndUserpass || false,
                    captcha,
                ),
            );
        },
        [dispatch, location, history, isRecoveryPassAndUserpass],
    );

    const handleForgotPassword = useCallback(
        (submitForm) => {
            dispatch(recoveryPasswordActions.setRecoveryPassAndUserpass());
            setTimeout(() => {
                if (typeof submitForm === "function") {
                    submitForm();
                }
            }, 100);
        },
        [dispatch],
    );

    const getLabels = useCallback((id) => {
        const keys = {
            documentTypeAndNumber: "login.step1.documentType.label",
            password: "recoveryUserpass.step1.password.label",
            continue: "global.continue",
            forgotPasswordToo: "recoveryUserpass.step1.forgotPasswordToo",
        };
        return i18n.get(keys[id] ?? id);
    }, []);

    const getText = useCallback((id) => i18n.get(id), []);

    const serverCaptchaError = fieldErrors?.captcha || "";
    const displayCaptchaError = captchaError || serverCaptchaError;

    return (
        <div ref={wrapperRef} style={{ width: "100%" }}>
            <RecoveryUserpassStep0
                initialValues={initialValues}
                validationSchema={validationSchema}
                handleSubmit={handleSubmit}
                handleForgotPassword={handleForgotPassword}
                getLabels={getLabels}
                getText={getText}
                documentTypeOptions={DOC_TYPES}
                submitLoading={recoveryAsyncPending}
                fieldErrors={fieldErrors ?? undefined}
            />
            {captchaPortalTarget &&
                ReactDOM.createPortal(
                    <div>
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={configUtil.get("core.auth.captcha.google.siteKey")}
                            onChange={handleCaptchaChange}
                            onExpired={() => {
                                captchaValueRef.current = "";
                            }}
                        />
                        {displayCaptchaError && (
                            <span
                                style={{
                                    color: "rgba(255, 89, 92, 1)",
                                    fontSize: 14,
                                    marginTop: 4,
                                    display: "block",
                                }}>
                                {displayCaptchaError}
                            </span>
                        )}
                    </div>,
                    captchaPortalTarget,
                )}
        </div>
    );
}

RecoveryUserpassStep0V3.propTypes = {
    dispatch: func.isRequired,
    location: shape({
        pathname: string,
        state: shape({
            document: string,
            documentType: string,
        }),
    }).isRequired,
    history: shape({ push: func }).isRequired,
    document: string,
    documentType: string,
    isRecoveryPassAndUserpass: bool,
    recoveryAsyncPending: bool,
    fieldErrors: objectOf(string),
    showCaptcha: bool,
    resetCaptcha: bool,
};

RecoveryUserpassStep0V3.defaultProps = {
    document: null,
    documentType: null,
    isRecoveryPassAndUserpass: false,
    recoveryAsyncPending: false,
    fieldErrors: undefined,
    showCaptcha: false,
    resetCaptcha: false,
};

const mapStateToProps = (state) => ({
    document: recoveryPasswordSelectors.getDocument(state),
    documentType: recoveryPasswordSelectors.getDocumentType(state),
    isRecoveryPassAndUserpass: recoveryPasswordSelectors.getRecoveryPassAndUserpass(state),
    recoveryAsyncPending: recoveryPasswordSelectors.getAsyncPending(state),
    fieldErrors: recoveryPasswordSelectors.getFormFieldErrors(state) ?? undefined,
    showCaptcha: recoveryPasswordSelectors.getShowCaptcha(state),
    resetCaptcha: recoveryPasswordSelectors.getResetCaptcha(state),
});

export default connect(mapStateToProps)(RecoveryUserpassStep0V3);
